/**
 * Planos de un proyecto (galería de OPUS ci) — helpers PUROS, sin DB ni IA.
 *
 * Cuatro preguntas que la galería le hace a cada archivo, en un solo lugar para que
 * la API y la pantalla respondan lo mismo:
 *   1. ¿Es un plano, y de qué tipo? (`tipoDePlanoPorNombre`)
 *   2. ¿Qué datos cortos se pueden decir de él? (`datosDelPlano`: "57 tipos · 2 pisos · 245 piezas")
 *   3. ¿Tiene algo que medir? (`planoSinNadaMedible`, para el aviso amarillo del DWG)
 *   4. ¿Cuánto le falta? (`avanceDelPlano` + `estadoDelPlano`: "Sin empezar" / "N renglones" / "Listo")
 *
 * Nada de esto decide por nombres de capa ni tablas de dominio: todo sale de la
 * extensión del archivo y de lo que el propio inventario del plano declara.
 */
import { seleccionesDe, type PlanoInventario, type PlanoSeleccion } from './domain.js';
import { contenidoMedible } from './planoRolArchivo.js';

/** Los tres tipos de plano que OPUS ci sabe trabajar. DXF cuenta como 'dwg' (mismo camino). */
export type TipoDePlano = 'ifc' | 'dwg' | 'pdf';

/** Tipo de plano por la extensión del archivo; null si no es un plano (un Excel, un Word, una foto…). */
export function tipoDePlanoPorNombre(nombre: string): TipoDePlano | null {
  if (/\.ifc$/i.test(nombre)) return 'ifc';
  if (/\.(dwg|dxf)$/i.test(nombre)) return 'dwg';
  if (/\.pdf$/i.test(nombre)) return 'pdf';
  return null;
}

/** Etiqueta del tipo, tal como va sobre la miniatura. */
export function etiquetaTipoDePlano(tipo: TipoDePlano, nombre: string): string {
  if (tipo === 'ifc') return 'MODELO 3D · IFC';
  if (tipo === 'dwg') return /\.dxf$/i.test(nombre) ? 'DIBUJO · DXF' : 'DIBUJO · DWG';
  return 'PDF';
}

const NUM = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });

function plural(n: number, uno: string, varios: string): string {
  return `${NUM.format(n)} ${n === 1 ? uno : varios}`;
}

/**
 * Cuántos ELEMENTOS tiene un modelo IFC: las piezas del inventario (una por elemento IFC de las clases
 * que se inventarían), NO las mallas que se dibujan (una ventana son 3–4 mallas: marco, vidrios; en
 * FZK-Haus 82 elementos son 245 mallas). Lo usan la ficha del plano y la barra de estado del visor, para
 * que digan el mismo número.
 */
export function elementosDelInventarioIfc(capas: PlanoInventario['capas']): number {
  return capas
    .filter((c) => c.categoria != null)
    .reduce((s, c) => s + (c.instancias?.length || c.medidas?.conteo || c.n_entidades || 0), 0);
}

/**
 * Línea corta de datos del plano, cuando ya se leyó. null = todavía no hay nada que decir.
 *  - IFC: tipos de elemento · pisos · piezas.
 *  - DWG/DXF: capas · piezas dibujadas (bloques insertados).
 *  - PDF: cantidades que se alcanzaron a leer de las láminas.
 */
export function datosDelPlano(tipo: TipoDePlano, inv: PlanoInventario | null | undefined): string | null {
  if (!inv) return null;
  const partes: string[] = [];
  if (tipo === 'ifc') {
    const capas = inv.capas.filter((c) => c.categoria != null);
    if (capas.length === 0) return null;
    partes.push(plural(capas.length, 'tipo', 'tipos'));
    // Pisos: solo los que el modelo nombra. "Varios niveles" no es un piso.
    const pisos = new Set(
      capas
        .map((c) => (c.nivel ?? '').trim())
        .filter((n) => n.length > 0 && n !== 'Varios niveles'),
    );
    if (pisos.size > 0) partes.push(plural(pisos.size, 'piso', 'pisos'));
    const piezas = elementosDelInventarioIfc(capas);
    if (piezas > 0) partes.push(plural(piezas, 'pieza', 'piezas'));
  } else if (tipo === 'dwg') {
    if (inv.capas.length > 0) partes.push(plural(inv.capas.length, 'capa', 'capas'));
    if (inv.total_inserts > 0) partes.push(plural(inv.total_inserts, 'pieza dibujada', 'piezas dibujadas'));
  } else {
    const n = (inv.cantidadesEstimadas ?? []).length;
    if (n > 0) partes.push(plural(n, 'cantidad leída', 'cantidades leídas'));
  }
  return partes.length > 0 ? partes.join(' · ') : null;
}

/**
 * ¿Es un dibujo (DWG/DXF) YA leído en el que casi no hay nada que medir? Señal geométrica
 * (`contenidoMedible`): ni piezas contables ni geometría con cantidad. Sin inventario todavía → false
 * (no se avisa de lo que no se sabe). Solo aplica a DWG/DXF: el IFC trae sus medidas y el PDF
 * tiene su propio aviso.
 */
export function planoSinNadaMedible(tipo: TipoDePlano, inv: PlanoInventario | null | undefined): boolean {
  if (tipo !== 'dwg' || !inv) return false;
  // Solo la geometría: una lámina de detalles con trazo medible no es "nada que medir" aunque se
  // proponga como referencia por sus notas de especificación.
  if (inv.estimadoVision && (inv.cantidadesEstimadas ?? []).length > 0) return false;
  const { bloquesContables, capaMedible } = contenidoMedible(inv);
  return !bloquesContables && !capaMedible;
}

/** Lo mínimo de un renglón de la hoja que hace falta para medir el avance. */
export interface RenglonParaAvance {
  rol: string;
  seleccion: PlanoSeleccion | null;
}

/**
 * Avance de un modelo IFC: cuántos de sus tipos de elemento ya tienen concepto principal
 * en la hoja. Solo IFC, porque ahí el modelo declara sus elementos (tipo = capa con
 * categoría y piezas) y "terminado" se puede medir. En DWG/PDF no hay una lista cerrada
 * de lo que hay que presupuestar → null (la galería solo cuenta renglones).
 */
export function avanceDelPlano(
  inv: PlanoInventario | null | undefined,
  renglones: readonly RenglonParaAvance[],
): { conConcepto: number; total: number } | null {
  if (!inv) return null;
  const elementos = inv.capas.filter((c) => c.categoria != null && (c.medidas?.conteo ?? 0) > 0);
  if (elementos.length === 0) return null;
  // Un renglón que junta varios tipos (los de `ademas`) los da a todos por presupuestados.
  const conPrincipal = new Set(
    renglones
      .filter((r) => r.rol === 'principal')
      .flatMap((r) => seleccionesDe(r))
      .filter((s) => s.tipo === 'capa')
      .map((s) => s.clave),
  );
  const conConcepto = elementos.filter((c) => conPrincipal.has(c.capa)).length;
  return { conConcepto, total: elementos.length };
}

export type EstadoDelPlano = 'sin_empezar' | 'en_curso' | 'listo';

/** Chip de estado de la ficha: "Sin empezar" / "N renglones" / "Listo". */
export function estadoDelPlano(
  renglones: number,
  avance: { conConcepto: number; total: number } | null,
): { estado: EstadoDelPlano; texto: string } {
  if (renglones <= 0) return { estado: 'sin_empezar', texto: 'Sin empezar' };
  if (avance && avance.total > 0 && avance.conConcepto >= avance.total) {
    return { estado: 'listo', texto: 'Listo' };
  }
  return { estado: 'en_curso', texto: plural(renglones, 'renglón', 'renglones') };
}
